import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../logo.png';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { WishlistContext } from '../context/WishlistContext';

const Navbar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, logout } = useContext(AuthContext);
  const { cartItems } = useContext(CartContext);
  const { wishlistItems } = useContext(WishlistContext);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logout();
      setIsLoggingOut(false);
    }, 1500); // 1.5s delay for better UX
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
    setTimeout(() => {
      setShowBanner(true);
    }, 30000); // Re-appears after 30 seconds
  };

  return (
    <>
      {isLoggingOut && (
        <div className="fixed inset-0 z-[999999] flex flex-col items-center justify-center pointer-events-none">
          <div className="bg-[#131418] border border-white/10 shadow-2xl p-8 rounded-3xl flex flex-col items-center justify-center min-w-[250px]">
            <div className="flex gap-4 mb-6 mt-2 h-12 items-center drop-shadow-2xl">
              <div className="candle"></div>
              <div className="candle"></div>
              <div className="candle"></div>
            </div>
            <h2 className="text-[14px] font-bold text-white tracking-widest uppercase animate-pulse drop-shadow-2xl">Logging Out...</h2>
          </div>
        </div>
      )}
    <header className="bg-surface/90 backdrop-blur-md font-label-md text-label-md docked full-width top-0 sticky z-50 border-b border-white/10 shadow-sm">
      {/* Top Promotional Banner */}
      {showBanner && (
        <div className="bg-[#1c1806] text-primary py-2 px-4 flex justify-center items-center relative text-[13px] font-bold tracking-wide border-b border-primary/20 animate-fade-in">
          <div className="flex items-center gap-2">
            <span>📈</span>
            <span>Get Premium EAs & Indicators At Up To 95% OFF. Starting with just 9$.</span>
          </div>
          <button 
            onClick={handleCloseBanner}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary hover:text-white transition-colors flex items-center justify-center"
            aria-label="Close banner"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      <div className="flex justify-between items-center w-full px-margin-mobile md:px-margin-desktop py-4 max-w-[1400px] mx-auto gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0 whitespace-nowrap">
          <img src={logo} alt="ForexBot.shop" className="h-8 w-auto" />
          <span className="font-display-lg-mobile text-headline-sm font-bold text-silver-text tracking-tight hidden sm:block">
            ForexBot<span className="text-primary">.shop</span>
          </span>
        </Link>
        
        {/* Mobile Hamburger Button */}
        <button 
          className="xl:hidden ml-auto p-2 text-silver-text hover:text-primary transition-colors flex items-center justify-center bg-[#1A1B20] border border-white/10 rounded-xl hover:bg-white/5"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open Mobile Menu"
        >
          <span className="material-symbols-outlined text-[20px]">menu</span>
        </button>
        <nav className="hidden xl:flex items-center space-x-6 text-[13px] whitespace-nowrap">
          <Link to="/" className={`nav-link ${currentPath === '/' ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>Home</Link>
          <Link to="/marketplace" className={`nav-link ${currentPath === '/marketplace' ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>Marketplace</Link>
          <Link to="/custom-ea" className={`nav-link ${currentPath === '/custom-ea' ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>Custom Algo Development</Link>
          {/* <Link to="/compare" className={`nav-link ${currentPath === '/compare' ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>Compare EA</Link> */}
          <Link to="/request" className={`nav-link ${currentPath === '/request' ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>Request EA</Link>
          
          {/* Explore Dropdown */}
          <div 
            className="relative group"
            onMouseEnter={() => setIsExploreOpen(true)}
            onMouseLeave={() => setIsExploreOpen(false)}
          >
            <button className={`nav-link flex items-center gap-1 ${currentPath === '/why-us' || isExploreOpen ? 'text-primary font-bold' : 'text-silver-text hover:text-primary transition-colors'}`}>
              Explore <span className="material-symbols-outlined text-[14px]">expand_more</span>
            </button>
            {isExploreOpen && (
              <div className="absolute top-full right-0 pt-4 w-48 z-50 animate-fade-in">
                <div className="bg-surface-container border border-white/10 rounded-xl shadow-2xl py-2">
                  <Link to="/why-us" className="block px-4 py-2 text-primary hover:bg-white/5 transition-colors font-bold text-[13px] whitespace-nowrap">Why Us</Link>
                  <Link to="/how-it-works" className="block px-4 py-2 text-silver-text hover:text-primary hover:bg-white/5 transition-colors text-[13px] whitespace-nowrap">How It Works</Link>
                  <Link to="/contact" className="block px-4 py-2 text-silver-text hover:text-primary hover:bg-white/5 transition-colors text-[13px] whitespace-nowrap">Contact Us</Link>
                </div>
              </div>
            )}
          </div>
        </nav>
        <div className="flex items-center space-x-3 shrink-0">
          
          {/* Wishlist Icon */}
          <Link to="/account/wishlist" className="relative p-2 text-silver-text hover:text-primary transition-colors flex items-center justify-center bg-[#1A1B20] border border-white/10 rounded-xl hover:bg-white/5">
            <span className="material-symbols-outlined text-[20px]">favorite</span>
            {wishlistItems && wishlistItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-[#131418] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(240,185,11,0.5)]">
                {wishlistItems.length}
              </span>
            )}
          </Link>

          {/* Cart Icon */}
          <Link to="/cart" className="relative p-2 text-silver-text hover:text-primary transition-colors flex items-center justify-center bg-[#1A1B20] border border-white/10 rounded-xl hover:bg-white/5">
            <span className="material-symbols-outlined text-[20px]">shopping_cart</span>
            {cartItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-[#131418] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(240,185,11,0.5)]">
                {cartItems.length}
              </span>
            )}
          </Link>

          {/* Account Dropdown */}
          <div 
            className="relative ml-2"
            onMouseEnter={() => setIsAccountOpen(true)}
            onMouseLeave={() => setIsAccountOpen(false)}
          >
            {!user ? (
              <button className="bg-gradient-to-r from-primary to-primary/80 text-on-primary font-bold px-4 py-2 rounded-full flex items-center gap-2 hover:shadow-[0_0_15px_rgba(240,185,11,0.5)] hover:-translate-y-0.5 transition-all duration-300 text-[13px] whitespace-nowrap border border-primary/50">
                <span className="material-symbols-outlined text-[16px]">person</span> Account <span className="material-symbols-outlined text-[16px]">expand_more</span>
              </button>
            ) : (
              <button className="bg-[#1A1B20] border border-white/10 text-white font-bold pl-1.5 pr-3 py-1 rounded-full flex items-center gap-2.5 hover:bg-white/5 transition-colors text-[13px]">
                <div className="w-6 h-6 rounded-full border border-primary text-primary flex items-center justify-center text-[10px] font-bold bg-primary/10">
                  {user.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                {user.fullName.split(' ')[0].toLowerCase()}
                <span className="material-symbols-outlined text-[16px] text-on-surface-variant">
                  {isAccountOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>
            )}

            {isAccountOpen && (
              <div className="absolute top-full right-0 pt-2 w-[190px] z-50 animate-fade-in">
                {!user ? (
                  <div className="bg-surface-container border border-white/10 rounded-lg shadow-2xl py-1.5">
                    <Link to="/login" className="px-3 py-1.5 text-primary hover:bg-white/5 transition-colors font-bold text-[12px] whitespace-nowrap flex items-center gap-2"><span className="material-symbols-outlined text-[14px]">login</span> Log In</Link>
                    <Link to="/signup" className="px-3 py-1.5 text-silver-text hover:text-primary hover:bg-white/5 transition-colors text-[12px] whitespace-nowrap flex items-center gap-2"><span className="material-symbols-outlined text-[14px]">person_add</span> Create Account</Link>
                  </div>
                ) : (
                  <div className="bg-[#18191d] border border-white/5 rounded-xl shadow-2xl py-1.5 text-[12px]">
                    {/* Profile Header */}
                    <div className="px-3 py-1.5">
                      <div className="font-bold text-white text-[13px] leading-tight">{user.fullName.toLowerCase()}</div>
                      <div className="text-on-surface-variant text-[11px] truncate leading-tight mt-0.5">{user.email}</div>
                    </div>
                    
                    <div className="h-px bg-white/5 my-1"></div>
                    
                    <div className="flex flex-col">
                      {user.role === 'admin' && (
                        <Link to="/admin" className={`px-3 py-1.5 transition-colors flex items-center gap-2 ${currentPath === '/admin' ? 'text-primary font-bold bg-white/5' : 'text-primary hover:bg-white/5'}`}>
                          <span className="material-symbols-outlined text-[14px]">admin_panel_settings</span> Admin Dashboard
                        </Link>
                      )}
                      <Link to="/account/dashboard" className={`px-3 py-1.5 transition-colors ${currentPath === '/account/dashboard' ? 'text-primary font-bold bg-white/5' : 'text-silver-text hover:text-primary hover:bg-white/5'}`}>
                        Dashboard
                      </Link>
                      <Link to="/account/wishlist" className={`px-3 py-1.5 transition-colors ${currentPath === '/account/wishlist' ? 'text-primary font-bold bg-white/5' : 'text-silver-text hover:text-primary hover:bg-white/5'}`}>
                        Wishlist
                      </Link>
                      <Link to="/account/orders" className={`px-3 py-1.5 transition-colors ${currentPath === '/account/orders' ? 'text-primary font-bold bg-white/5' : 'text-silver-text hover:text-primary hover:bg-white/5'}`}>
                        Orders
                      </Link>
                      <Link to="/account/expert-advisors" className={`px-3 py-1.5 transition-colors ${currentPath === '/account/expert-advisors' ? 'text-primary font-bold bg-white/5' : 'text-silver-text hover:text-primary hover:bg-white/5'}`}>
                        My Expert Advisors
                      </Link>
                      <Link to="/account/custom-requests" className={`px-3 py-1.5 transition-colors ${currentPath === '/account/custom-requests' ? 'text-primary font-bold bg-white/5' : 'text-silver-text hover:text-primary hover:bg-white/5'}`}>
                        Custom Requests
                      </Link>
                    </div>

                    <div className="h-px bg-white/5 my-1"></div>
                    
                    <button onClick={handleLogout} disabled={isLoggingOut} className="w-full text-left px-3 py-1.5 text-[#EF4444] hover:bg-white/5 transition-colors font-bold text-[12px] flex items-center justify-between">
                      {isLoggingOut ? 'Logging out...' : 'Logout'}
                      {isLoggingOut && <span className="material-symbols-outlined text-[14px] animate-spin">autorenew</span>}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>

    {/* Full Screen Mobile Menu Overlay */}
    {isMobileMenuOpen && (
      <div className="fixed inset-0 z-[1000000] bg-[#131418] flex flex-col p-6 overflow-y-auto animate-fade-in xl:hidden">
        <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
          <Link to="/" className="flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
            <img src={logo} alt="ForexBot.shop" className="h-8 w-auto" />
            <span className="text-headline-sm font-bold text-silver-text tracking-tight">ForexBot.shop</span>
          </Link>
          <button 
            className="p-2 text-silver-text hover:text-primary bg-[#1A1B20] border border-white/10 rounded-xl"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="material-symbols-outlined text-[24px]">close</span>
          </button>
        </div>
        
        <nav className="flex flex-col gap-6 text-[18px] font-bold">
          <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className={`nav-link ${currentPath === '/' ? 'text-primary' : 'text-silver-text'}`}>Home</Link>
          <Link to="/marketplace" onClick={() => setIsMobileMenuOpen(false)} className={`nav-link ${currentPath === '/marketplace' ? 'text-primary' : 'text-silver-text'}`}>Marketplace</Link>
          <Link to="/custom-ea" onClick={() => setIsMobileMenuOpen(false)} className={`nav-link ${currentPath === '/custom-ea' ? 'text-primary' : 'text-silver-text'}`}>Custom Algo Development</Link>
          <Link to="/request" onClick={() => setIsMobileMenuOpen(false)} className={`nav-link ${currentPath === '/request' ? 'text-primary' : 'text-silver-text'}`}>Request EA</Link>
          
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-4 text-[16px] text-on-surface-variant font-normal">
            <span className="uppercase tracking-widest text-[12px] font-bold text-silver-text">Explore</span>
            <Link to="/why-us" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-primary transition-colors">Why Us</Link>
            <Link to="/how-it-works" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-primary transition-colors">How It Works</Link>
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-primary transition-colors">Contact Us</Link>
          </div>
        </nav>
      </div>
    )}
    </>
  );
};

export default Navbar;
